import Container from '@atoms/Container';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import paddingDefault from '@utils/paddingDefault';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import { graphql } from 'gatsby';
import React from 'react';
import { colors } from '../primitives/colors';
import RowSet from '../primitives/grid/row-set';
import { StyledButtonAsLink } from '../primitives/styled-button';
import Typography from '../primitives/typography';
import Breadcrumbs from '../sections/Breadcrumbs';

const TextBanner = ({ data }) => {
    const {
        headline, subheadline, copy, link, settings, spacing,
    } = data;

    const hasBreadcrumbs = !!settings?.has_breadcrumbs;
    const hideBackground = !!settings?.hide_background;
    const darkMode = !!settings?.dark_mode;
    const headlineCenter = !!settings?.headline_center;
    const textCenter = !!settings?.copy_center;
    const buttonCenter = copy ? !!textCenter : !!headlineCenter;

    return (
        <div css={[paddingDefault(spacing), styles.background(darkMode, hideBackground)]}>
            <Container>
                <div css={styles.contentWrap(textCenter)}>
                    <RowSet breakpoints={{
                        dt: { between: 32 },
                        tb: { between: 24 },
                        mb: { between: 16 },
                    }}
                    >
                        {hasBreadcrumbs && <div css={styles.breadcrumbs}><Breadcrumbs /></div>}
                        {headline && (
                            <Typography
                                css={{
                                    paddingBottom: !!copy && '1.25rem',
                                    textAlign: headlineCenter && 'center',
                                    maxWidth: headlineCenter ? 'unset' : '900px',
                                }}
                                dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(headline) }}
                                fontSize={{
                                    dt: '5xl',
                                    tb: '4xl',
                                    mb: '3xl',
                                }}
                                reducedLetterSpacing
                                fontWeight="regular"
                            />
                        )}
                        {subheadline && (
                            <h4
                                css={[RichTextEditorStyles, styles.subheading(headlineCenter)]}
                                dangerouslySetInnerHTML={{ __html: subheadline }}
                            />
                        )}
                        <div
                            css={[RichTextEditorStyles, styles.copy(textCenter)]}
                            dangerouslySetInnerHTML={{ __html: copy }}
                        />
                        {!!link && !!link?.href && !!link?.title && (
                            <div css={styles.buttonWrapper(buttonCenter)}>
                                <StyledButtonAsLink to={link.href}>{link.title}</StyledButtonAsLink>
                            </div>
                        )}
                    </RowSet>
                </div>
            </Container>
        </div>
    );
};

const styles = {
    background: (isDarkMode, isHideBackground) => ({
        backgroundColor: isDarkMode
            ? colors.shadesBlack
            : isHideBackground
                ? colors.shadesWhite
                : colors.grey100,
        color: isDarkMode && colors.shadesWhite,
    }),
    contentWrap: textCenter => ({
        '@media (min-width: 1024px)': {
            textAlign: textCenter && 'center',
        },
    }),
    subheading: headlineCenter => ({
        marginBottom: '1.25rem',
        fontSize: '1.25rem',
        lineHeight: 1,
        maxWidth: headlineCenter ? 'unset' : '900px',
        textAlign: headlineCenter && 'center',
        '@media (min-width: 1024px)': {
            fontSize: '1.875rem',
            lineHeight: '2.25rem',
        },
    }),
    copy: textCenter => ({
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
        whiteSpace: 'pre-line',
        textAlign: textCenter && 'center',
        '@media (min-width: 1024px)': {
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
            maxWidth: textCenter && '48rem',
            marginRight: textCenter && 'auto',
            marginLeft: textCenter && 'auto',
        },
    }),
    buttonWrapper: textCenter => ({
        display: 'flex',
        marginTop: '1.75rem',
        justifyContent: textCenter && 'center',
        '@media (min-width: 1024px)': {
            justifyContent: 'center',
        },
    }),
    breadcrumbs: {
        marginBottom: '0.75rem',
    },
};

export const query = graphql`
  fragment TextBannerData on CmsPageBuilderBlocks {
    text_banner {
      headline
      subheadline
      copy
      spacing {
        top_gutter
        bottom_gutter
      }
      link {
        href
        title
      }
      settings {
        headline_center
        copy_center
        dark_mode
        has_breadcrumbs
        hide_background
      }
    }
  }
`;

export default TextBanner;
